import { Flex, Skeleton, LogoAnimated } from '@nula/core-ui';
import styles from './editor-skeleton.module.css';

function seededRand(seed: number) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

interface SkeletonLine {
  width: number;
  height: number;
  marginLeft: number;
  marginBottom: number;
  isBlock?: boolean;
}

function textLines(rand: () => number, count: number): SkeletonLine[] {
  return Array.from({ length: count }, (_, i) => {
    const isLast = i === count - 1;
    return {
      width: isLast ? 30 + rand() * 35 : 75 + rand() * 25,
      height: 10 + rand() * 5,
      marginLeft: 0,
      marginBottom: isLast ? 28 : 5 + rand() * 4, // paragraph break after last line
    };
  });
}

function imageBlock(rand: () => number): SkeletonLine {
  return {
    width: 100,
    height: 180 + rand() * 60,
    marginLeft: 0,
    marginBottom: 28,
    isBlock: true,
  };
}

function buildLines(): SkeletonLine[] {
  const rand = seededRand(0xdeadbeef);
  return [
    ...textLines(rand, 4),
    ...textLines(rand, 3),
    ...textLines(rand, 5),
    imageBlock(rand),
    ...textLines(rand, 3),
    ...textLines(rand, 4),
    ...textLines(rand, 3),
    ...textLines(rand, 5),
    ...textLines(rand, 2),
    ...textLines(rand, 4),
  ];
}

const LINES = buildLines();

export function EditorSkeleton() {
  return (
    <Flex vertical className={styles.editorRoot}>
      {/* Header — title placeholder + actionbar placeholder */}
      <Flex className={styles.headerBar}>
        <Flex align="center" justify="space-between" className={styles.headerContent}>
          <div className={styles.skeletonTitleSlot}>
            <Skeleton active paragraph={false} title={{ width: '40%' }} />
          </div>
          <div className={styles.skeletonActionbarSlot}>
            <Skeleton.Button active />
            <Skeleton.Button active />
            <Skeleton.Button active />
          </div>
        </Flex>
      </Flex>

      {/* Scroll area — overflow hidden so skeleton never scrolls */}
      <Flex vertical className={styles.skeletonScrollArea}>
        <Flex vertical className={styles.scrollContent}>
          <div className={styles.skeletonBody}>
            {LINES.map((line, i) =>
              line.isBlock ? (
                <div
                  key={i}
                  className={styles.skeletonBlock}
                  style={{
                    '--skel-w': `${line.width}%`,
                    '--skel-h': `${line.height}px`,
                    '--skel-ml': `${line.marginLeft}%`,
                    '--skel-mb': `${line.marginBottom}px`,
                  } as React.CSSProperties}
                >
                  <LogoAnimated size={300} loop duration={2400} holdDuration={600} className={styles.skeletonBlockLogo} />
                </div>
              ) : (
                <div
                  key={i}
                  className={styles.skeletonLine}
                  style={{
                    '--skel-w': `${line.width}%`,
                    '--skel-h': `${line.height}px`,
                    '--skel-ml': `${line.marginLeft}%`,
                    '--skel-mb': `${line.marginBottom}px`,
                  } as React.CSSProperties}
                />
              )
            )}
          </div>
        </Flex>
      </Flex>
    </Flex>
  );
}
